import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { api, setSessionExpiredHandler, tokens } from '../api/client';
import type { TokenResponse, User } from '../api/client';

type AuthState = {
  user: User | null;
  loading: boolean;
  signIn(email: string, password: string, captchaToken?: string): Promise<void>;
  signUp(email: string, name: string, password: string, captchaToken?: string): Promise<void>;
  signOut(): Promise<void>;
};

const AuthContext = createContext<AuthState | null>(null);

/**
 * Session state.
 *
 * The access token is the source of truth for "signed in"; `user` is fetched
 * once on mount to render the name and confirm the token is still valid. A
 * token that has been revoked server-side is discarded here rather than leaving
 * the UI showing a signed-in shell that fails on every request.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const clearSession = useCallback(() => {
    tokens.clear();
    setUser(null);
  }, []);

  useEffect(() => {
    setSessionExpiredHandler(clearSession);
    return () => setSessionExpiredHandler(null);
  }, [clearSession]);

  useEffect(() => {
    if (!tokens.access()) {
      setLoading(false);
      return;
    }
    api.auth
      .me()
      .then(setUser)
      .catch(clearSession)
      .finally(() => setLoading(false));
  }, [clearSession]);

  const adopt = useCallback((result: TokenResponse) => {
    tokens.save(result.access_token, result.refresh_token);
    setUser(result.user);
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      loading,
      signIn: async (email, password, captchaToken) =>
        adopt(await api.auth.login({ email, password, captcha_token: captchaToken })),
      signUp: async (email, name, password, captchaToken) =>
        adopt(await api.auth.register({ email, name, password, captcha_token: captchaToken })),
      signOut: async () => {
        // Revoking the refresh token is best-effort: the local session is
        // cleared either way, and a network failure must not strand the user
        // in a signed-in-looking state.
        await api.auth.logout().catch(() => undefined);
        clearSession();
      },
    }),
    [user, loading, adopt, clearSession],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside an AuthProvider');
  return context;
}
